/* Racepack Fuel Planner v4.12.3 — carb-gated, sodium-led Top Match ranking.
   Runtime optimisations: cached plan-fit calculations, fewer repeated eligibility/pack passes,
   one currency formatter per instance, and low-priority lazy product images. */
(() => {
  'use strict';

  if (customElements.get('racepack-fuel-planner')) return;

  customElements.define('racepack-fuel-planner', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.$ = selector => this.querySelector(selector);
      this.engine = window.RacepackFuelEngine;

      if (!this.engine) {
        const status = this.$('[data-status]');
        if (status) status.textContent = 'The planner engine could not load. Please contact Racepack.';
        return;
      }

      const configNode = this.$('[data-config]');
      const statusNode = this.$('[data-status]');

      if (!configNode) {
        if (statusNode) statusNode.textContent = 'The planner could not load. Please contact Racepack.';
        return;
      }

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch {
        if (statusNode) statusNode.textContent = 'The planner could not load. Please contact Racepack.';
        return;
      }

      this.products = Array.isArray(this.config.products)
        ? this.config.products.filter(product => product && typeof product === 'object')
        : [];

      try {
        this.currencyFormatter = new Intl.NumberFormat(this.config.locale, {
          style: 'currency',
          currency: this.config.currency
        });
      } catch {
        this.currencyFormatter = null;
      }

      // Cache expensive pack/fit calculations for the active plan.
      // The cache is cleared whenever answers or the generated plan change.
      this.fitCache = new Map();

      this.form = this.$('form');

      if (!this.form) {
        if (statusNode) statusNode.textContent = 'The planner form could not load. Please contact Racepack.';
        return;
      }
      this.step = 0;
      this.plan = null;
      this.selection = null;
      this.baseProduct = null;
      this.sodium = null;
      this.busy = false;
      this.storageKey = 'racepack:fuel-planner:v4.11.7';
      this.dataset.plannerVersion = '4.12.3';
      this.cartSelection = null;
      // Planner ownership is stored as a browser-side quantity ledger rather
      // than Shopify line-item properties. This lets normal and planner adds
      // of the same exact variant merge into one Shopify cart line.
      this.plannerLedger = {};
      this.cachedReferenceVariantId = null;
      this.restoredView = 'form';
      this.modalProduct = null;
      this.modalFit = null;
      this.modalRows = [];
      this.modalFamilyKey = '';
      this.modalEditing = false;
      this.lastFocusedElement = null;

      // Product-browser UI state must exist before filters or results render.
      this.productPool = [];
      this.productFilters = {
        format: 'all',
        brand: 'all',
        caffeine: 'all',
        sort: 'recommended'
      };
      this.showAllProducts = false;

      this.abort = new AbortController();

      // The v4 planner is format-neutral unless a future visible format selector is added.
      // This prevents the legacy hidden default of "gel" from silently excluding chews/drinks.
      if (this.form.elements.format?.type === 'hidden') {
        this.form.elements.format.value = 'any';
      }

      this.addEventListener('click', e => this.onClick(e), { signal: this.abort.signal });

      this.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !this.$('[data-fuel-modal]')?.hidden) {
          e.preventDefault();
          this.closeFuelModal();
        }
      }, { signal: this.abort.signal });

      this.form.addEventListener('submit', e => {
        e.preventDefault();
        this.next();
      }, { signal: this.abort.signal });

      this.form.addEventListener('change', () => {
        this.syncChoices();
        const runningFields = this.$('[data-running-fields]');
        if (runningFields) runningFields.hidden = this.form.elements.sport.value !== 'running';
        this.syncDuration();

        if (this.fitCache) this.fitCache.clear();

        if (this.plan) {
          const results = this.$('[data-results]');
          const preview = this.$('[data-preview]');
          if (results) results.hidden = true;
          if (preview) preview.hidden = false;
          this.plan = null;
          this.selection = null;
          this.baseProduct = null;
          this.sodium = null;
        }
        this.syncProductCaffeineFilterVisibility();
        this.persistPlannerState('form');
      }, { signal: this.abort.signal });

      ['[data-product-brand]', '[data-product-caffeine]', '[data-product-sort]'].forEach(selector => {
        const control = this.$(selector);
        if (!control) return;
        control.addEventListener('change', () => {
          if (selector.includes('brand')) this.productFilters.brand = control.value;
          if (selector.includes('caffeine')) this.productFilters.caffeine = control.value;
          if (selector.includes('sort')) this.productFilters.sort = control.value;
          this.showAllProducts = false;
          this.renderProductBrowser();
        }, { signal: this.abort.signal });
      });

      const distance = new URLSearchParams(location.search).get('distance');
      if (['21.1', '42.2', '5', '10', 'other'].includes(distance)) {
        this.form.elements.distance.value = distance;
        this.form.elements.sport.value = 'running';
      }

      this.syncChoices();
      this.syncDuration();
      this.showStep(0, false);
      this.restorePlannerState();

      // Remove the legacy Recommended Pack side panel if stale theme markup is still present.
      const legacyPackTitle = this.$('[data-recommended-pack-title]');
      const legacyPackPanel = legacyPackTitle?.closest('aside, section, article');
      if (legacyPackPanel) legacyPackPanel.remove();

      this.pageShowHandler = () => {
        this.reconcilePlannerCart();
        this.refreshCartChrome();
      };
      this.pageHideHandler = () => this.persistPlannerState();
      window.addEventListener('pageshow', this.pageShowHandler);
      window.addEventListener('pagehide', this.pageHideHandler);

      // Keep planner selection state in sync when the customer changes the cart
      // from Dawn's slide-out drawer or another theme cart surface.
      this.externalCartSync = () => {
        clearTimeout(this.externalCartSyncTimer);
        this.externalCartSyncTimer = setTimeout(() => {
          this.reconcilePlannerCart();
        }, 80);
      };

      // Preferred path: Dawn's pub/sub cartUpdate event.
      try {
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.cartUpdateUnsubscribe = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            () => this.externalCartSync()
          );
        }
      } catch (_) {}

      // Fallback for customised Dawn themes: observe the existing drawer DOM.
      const liveCartDrawer = document.querySelector('cart-drawer');
      if (liveCartDrawer && typeof MutationObserver !== 'undefined') {
        this.cartDrawerObserver = new MutationObserver(() => this.externalCartSync());
        this.cartDrawerObserver.observe(liveCartDrawer, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      // Restore the generated result view after form values have been restored.
      if (this.restoredView === 'results') {
        queueMicrotask(() => this.next({ restore: true }));
      }
      this.reconcilePlannerCart();

      if (this.config.designMode && !this.products.some(p => this.engine.validProduct(p))) {
        const editorNote = this.$('[data-editor-note]');
        if (editorNote) editorNote.hidden = false;
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.ready = false;
      this.hydrationListener = false;
      this.productPool = [];
      this.productFilters = { format: 'all', brand: 'all', caffeine: 'all', sort: 'recommended' };
      this.showAllProducts = false;
      if (this.fitCache) this.fitCache.clear();
      if (this.pageShowHandler) window.removeEventListener('pageshow', this.pageShowHandler);
      if (this.pageHideHandler) window.removeEventListener('pagehide', this.pageHideHandler);
      if (typeof this.cartUpdateUnsubscribe === 'function') {
        try { this.cartUpdateUnsubscribe(); } catch (_) {}
      }
      if (this.cartDrawerObserver) this.cartDrawerObserver.disconnect();
      if (this.externalCartSyncTimer) clearTimeout(this.externalCartSyncTimer);
      this.cartUpdateUnsubscribe = null;
      this.cartDrawerObserver = null;
    }

    node(tag, text, className) {
      const el = document.createElement(tag);
      if (text !== undefined) el.textContent = text;
      if (className) el.className = className;
      return el;
    }

    money(cents) {
      const value = Number(cents || 0) / 100;
      if (this.currencyFormatter) return this.currencyFormatter.format(value);
      return value.toFixed(2) + ' ' + this.config.currency;
    }

    displayBrand(product) {
      if (!product) return '';
      const labels = {
        gu: 'GU',
        sis: 'SiS',
        maurten: 'Maurten',
        aminovital: 'Amino VITAL',
        tailwind: 'Tailwind',
        saltstick: 'SaltStick',
        crampfix: 'CrampFix',
        nuun: 'Nuun',
        skratch: 'Skratch Labs',
        high5: 'HIGH5',
        lmnt: 'LMNT',
        clif: 'CLIF',
        gatorade: 'Gatorade',
        myprotein: 'Myprotein',
        beetit: 'Beet It Sport',
        honeystinger: 'Honey Stinger',
        ote: 'OTE',
        hammer: 'Hammer Nutrition',
        cadence: 'Cadence Hydration',
        pocari: 'Pocari',
        injelly: 'in Jelly'
      };
      return product.brandLabel || labels[String(product.brand || '').toLowerCase()] || product.brand || '';
    }

    displayTitle(product) {
      const title = String(product?.title || 'Fuel product').trim();
      return title
        .replace(/\s*\|\s*Running\s*&\s*Endurance(?:\s*Fuel)?\s*$/i, '')
        .trim();
    }

    customerNote(note) {
      if (!note) return '';
      return String(note)
        .split(/(?<=[.!?])\s+/)
        .filter(sentence => !/(shopify|metafield|serving basis|stored as|variant-level|backend|technical)/i.test(sentence))
        .join(' ')
        .trim();
    }

    purchaseCopy(pack, product) {
      if (!pack || !product) return '';
      if (pack.toBuy <= 0) return 'No extra purchase needed';

      const packWord = pack.units === 1 ? 'pack' : 'packs';
      const servingWord = product.servings === 1 ? 'serving' : 'servings';
      return `Buy ${pack.units} ${packWord} (${product.servings} ${servingWord} each)`;
    }

    syncChoices() {
      this.querySelectorAll('.rf-choice input').forEach(input => {
        input.closest('.rf-choice').classList.toggle('is-selected', input.checked);
      });
    }

    scroll(el) {
      if (!el) return;
      el.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    showToast(message) {
      const toast = this.$('[data-toast]');
      if (!toast) return;

      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.hidden = true;
      }, 2600);
    }

    syncDuration() {
      const f = this.form.elements;
      const running = f.sport.value === 'running';

      const bodyMass = this.$('[data-body-mass]');
      if (bodyMass) bodyMass.hidden = false;
      if (f.weight) {
        f.weight.disabled = false;
        f.weight.required = true;
      }
      if (f.height) {
        f.height.disabled = false;
        f.height.required = true;
      }

      const half = running && f.distance.value === '21.1';
      const halfBand = this.$('[data-half-band]');
      const halfExact = this.$('[data-half-exact]');
      const otherDuration = this.$('[data-other-duration]');
      if (halfBand) halfBand.hidden = !half;
      if (halfExact) halfExact.hidden = !half;
      if (otherDuration) otherDuration.hidden = half;

      if (f.band) f.band.disabled = !half;
      if (f.finishMinutes) f.finishMinutes.disabled = !half;
      if (f.minutes) f.minutes.disabled = half;

      const bandValue = f.band?.value || '91-120';
      const bounds = {
        '60-90': [60, 90, 75],
        '91-120': [91, 120, 105],
        '121-150': [121, 150, 135],
        '151+': [151, 720, 180]
      }[bandValue] || [91, 120, 105];

      if (f.finishMinutes) {
        f.finishMinutes.min = bounds[0];
        f.finishMinutes.max = bounds[1];
        f.finishMinutes.required = half && bandValue === '151+';
        f.finishMinutes.placeholder = bandValue === '151+'
          ? 'Required — e.g. 180'
          : 'Optional — using ' + bounds[2] + ' minutes';
      }

      const bandNote = this.$('[data-band-note]');
      if (bandNote) {
        bandNote.textContent = bandValue === '151+'
          ? 'Enter a finish time of 151–720 minutes.'
          : 'Optional exact time between ' + bounds[0] + ' and ' + bounds[1] + ' minutes.';
      }

      const custom = !half && f.minutes?.value === 'custom';
      const customDuration = this.$('[data-custom-duration]');
      if (customDuration) customDuration.hidden = !custom;
      if (f.customMinutes) f.customMinutes.disabled = !custom;
    }

    showStep(index, focus = true) {
      // v4 keeps the questionnaire on one page. Results form the second visible stage.
      this.step = 0;
      this.querySelectorAll('[data-step]').forEach(el => { el.hidden = false; });

      this.querySelectorAll('[data-progress]').forEach(el => {
        const isInputStage = el.dataset.progress === '0';
        el.classList.toggle('is-current', isInputStage);
        el.classList.remove('is-complete');
        if (isInputStage) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });

      const back = this.$('[data-back]');
      const next = this.$('[data-next]');
      const nextNote = this.$('[data-next-note]');
      const status = this.$('[data-status]');
      if (back) back.hidden = true;
      if (next) next.textContent = 'GENERATE MY FUEL PLAN →';
      if (nextNote) nextNote.textContent = 'Final step — click above to see your personalised fuel, sodium and product plan.';
      if (status) status.textContent = '';

      if (focus) {
        const heading = this.$('[data-step="0"] h3');
        if (heading) heading.focus({ preventScroll: true });
        this.scroll(this.$('[data-wizard]'));
      }
    }

    next(options = {}) {
      for (const el of this.form.querySelectorAll('input,select')) {
        if (!el.disabled && !el.checkValidity()) {
          const details = el.closest('details');
          if (details) details.open = true;
          el.reportValidity();
          return;
        }
      }

      const fields = Object.fromEntries(new FormData(this.form));
      if (fields.minutes === 'custom') fields.minutes = fields.customMinutes;
      if (!fields.minutes) fields.minutes = 105;
      fields.external = fields.hasFuel === 'yes' ? fields.external : 0;

      // Stay format-neutral while the UI has no visible format preference control.
      if (this.form.elements.format?.type === 'hidden') fields.format = 'any';

      try {
        this.input = fields;
        this.plan = this.engine.calculate(fields);
        const preview = this.$('[data-preview]');
        const results = this.$('[data-results]');
        if (preview) preview.hidden = true;
        if (results) results.hidden = false;

        this.querySelectorAll('[data-progress]').forEach(el => {
          const isResultsStage = el.dataset.progress !== '0';
          el.classList.toggle('is-current', isResultsStage);
          el.classList.toggle('is-complete', !isResultsStage);
          if (isResultsStage) el.setAttribute('aria-current', 'step');
          else el.removeAttribute('aria-current');
        });

        this.renderResults();
        this.persistPlannerState('results');
        if (!options.restore) {
          const heading = this.$('[data-results-heading]');
          if (heading) heading.focus({ preventScroll: true });
          this.scroll(this.$('[data-results]'));
        }
      } catch (error) {
        const status = this.$('[data-status]');
        if (status) status.textContent = error.message;
      }
    }

    title() {
      const sports = {
        running: 'Running',
        cycling: 'Cycling',
        hyrox: 'HYROX',
        swimming: 'Swimming',
        gym: 'Gym & fitness',
        other: 'Your session'
      };

      const distances = {
        '21.1': 'Half marathon',
        '42.2': 'Marathon',
        '5': '5 km run',
        '10': '10 km run',
        other: 'Running'
      };

      return this.input.sport === 'running'
        ? distances[this.input.distance] || 'Running'
        : sports[this.input.sport];
    }

    renderResults() {
      if (this.fitCache) this.fitCache.clear();
      const p = this.plan;

      this.$('[data-results-heading]').textContent = 'YOUR ' + this.title().toUpperCase() + ' FUEL PLAN';
      this.$('[data-tags]').textContent = `${this.title()} · ${p.minutes} minutes · ${this.input.caffeine === 'no' ? 'No caffeine' : 'Caffeine options allowed'}`;

      this.$('[data-answer-summary]').textContent =
        `Caffeine: ${this.input.caffeine === 'yes' ? 'Yes' : 'No'} · ` +
        `Sensitive stomach: ${this.input.sensitive === 'yes' ? 'Yes' : 'No'} · ` +
        `Intensity: ${this.input.effort || 'steady'} · Goal: ${this.input.purpose} · ` +
        `Conditions: ${this.input.conditions === 'hot' ? 'Hot / humid' : 'Mild'} · ` +
        `Fuel experience: ${this.input.experience === 'new' ? 'New / unsure' : 'Practised'} · ` +
        `Body mass: ${this.input.weight} kg · ` +
        `Height: ${this.input.height} cm · ` +
        `Water bottle: ${this.input.carryBottle === 'no' ? 'No' : 'Yes'} · ` +
        `Gels already owned: ${p.ownedGels || 0} · ` +
        `Water: ${this.input.water === 'yes' ? 'Available' : 'Unconfirmed'}.`;

      this.$('[data-bmi-result]').textContent = p.bmi.toFixed(1);

      this.$('[data-target]').textContent = p.rateLow === p.rateHigh
        ? p.rateHigh + ' g/h'
        : p.rateLow + '–' + p.rateHigh + ' g/h';
      this.$('[data-total]').textContent = p.totalLow === p.totalHigh
        ? p.totalHigh + ' g'
        : p.totalLow + '–' + p.totalHigh + ' g';
      this.$('[data-existing]').textContent = p.external + ' g';
      this.$('[data-remaining]').textContent = p.remainingLow === p.remainingHigh
        ? p.remainingHigh + ' g'
        : p.remainingLow + '–' + p.remainingHigh + ' g';
      this.$('[data-fuel-calories]').textContent = p.fuelCaloriesLow === p.fuelCaloriesHigh
        ? p.fuelCaloriesHigh + ' kcal'
        : p.fuelCaloriesLow + '–' + p.fuelCaloriesHigh + ' kcal';
      const sodiumTotalRange = p.sodiumTotalRange || [p.sodiumTotal, p.sodiumTotal];
      this.$('[data-sodium-target]').textContent =
        sodiumTotalRange[0] === sodiumTotalRange[1]
          ? sodiumTotalRange[0] + ' mg'
          : sodiumTotalRange.join('–') + ' mg';
      this.$('[data-fluid-total]').textContent = p.sodiumRange
        ? p.sodiumRange.join('–') + ' mg/h'
        : Math.round(p.sodiumRate) + ' mg/h';
      this.$('[data-sodium-existing]').textContent = p.externalSodium + ' mg';
      const sodiumRemainingLow = Math.max(0, sodiumTotalRange[0] - p.externalSodium);
      const sodiumRemainingHigh = Math.max(0, sodiumTotalRange[1] - p.externalSodium);
      this.$('[data-sodium-remaining]').textContent =
        sodiumRemainingLow === sodiumRemainingHigh
          ? sodiumRemainingHigh + ' mg'
          : sodiumRemainingLow + '–' + sodiumRemainingHigh + ' mg';
      this.$('[data-energy-burn]').textContent = p.energyBurn == null
        ? '—'
        : p.energyBurn + ' kcal';

      this.$('[data-time-assumption]').textContent =
        (p.band ? 'Finish band: ' + p.band + ' minutes. ' : '') +
        'Quantities calculated for ' + p.minutes + ' minutes. ' +
        'Fuel calories cover during-race carbohydrate; they do not replace total energy used. ' +
        (p.energyBurn == null
          ? 'Energy-use estimate is only shown for running distances.'
          : 'Energy estimate uses 1 kcal/kg/km; terrain, running economy and conditions vary.');

      this.$('[data-range]').textContent = p.minutes <= 75 && p.target
        ? `For 45–75 minutes, published guidance supports small carbohydrate amounts or mouth sensing rather than a fixed hourly dose. Racepack uses 0–${p.rateHigh} g/h only as an optional practical planning band for these answers.`
        : p.target
          ? `Two replenishment targets are used together: carbohydrate ${p.rateLow}–${p.rateHigh} g/h and sodium ${p.sodiumRange ? p.sodiumRange.join('–') : Math.round(p.sodiumRate)} mg/h. Fuel products must first fit the carbohydrate plan safely. Once products are inside the carbohydrate target range, Top Match prioritises sodium fit between them; small differences around the carbohydrate midpoint become only a tie-breaker. The electrolyte step can still complete any remaining sodium without double-counting carbohydrate from drink mixes or chews.`
          : `No during-session sports fuel is automatically recommended for these answers. Sodium remains a separate ${p.sodiumRange ? p.sodiumRange.join('–') : Math.round(p.sodiumRate)} mg/h planning range.`;

      const notes = this.$('[data-notes]');
      notes.replaceChildren();
      p.notes.forEach(n => notes.append(this.node('li', n)));

      this.$('[data-hydration]').textContent =
        (this.input.conditions === 'hot'
          ? 'In heat and humidity, confirm water access and consider your own sweat losses. '
          : '') +
        (this.input.sweat === 'heavy'
          ? 'You selected heavy sweating: plan regular fluid access and use your tested electrolyte approach. '
          : this.input.sweat === 'unsure'
            ? 'If you are unsure of your sweat rate, assess it in training before choosing a fixed hydration dose. '
            : 'Your selected sweat level is a subjective guide, not a measured sweat rate. ') +
        'Drink according to thirst, conditions and your tested hydration plan. Follow mixing instructions on drink products. ' +
        'Your sodium starting estimate below is generated from time, weather and intensity. It is not a measurement of your sweat losses.';

      this.$('[data-during]').textContent = p.target
        ? 'Spread carbohydrate across the session and count gels, chews, drinks and aid-station fuel together. Aim to stay within your planning range rather than chasing an exact gram total.'
        : 'Water and a familiar food routine may be sufficient for this session.';

      const pool = p.remainingLow > 0
        ? this.engine.eligible(this.products, this.input, p)
        : [];

      this.productPool = [...pool]
        .filter(product => product && product.id != null)
        .sort((a, b) => this.productScore(a) - this.productScore(b));

      // A newly generated plan always starts with a deliberate customer choice.
      // Do not silently auto-select the first matching product.
      this.productFilters = {
        format: 'all',
        brand: 'all',
        caffeine: 'all',
        sort: 'recommended'
      };
      this.showAllProducts = false;
      this.baseProduct = null;
      this.selection = null;

      const shopping = this.$('[data-shopping]');
      const packEmpty = this.$('[data-pack-empty]');
      const cartStatus = this.$('[data-cart-status]');
      const cartCheck = this.$('[data-cart-check]');

      if (shopping) shopping.hidden = true;
      if (packEmpty) {
        packEmpty.hidden = false;
        packEmpty.textContent = p.remainingLow === 0
          ? 'Your current plan does not require additional race fuel. Review the sodium section and anything you already plan to carry.'
          : 'Choose one fuel option above. We’ll then show your serving schedule, how much to buy and the hydration recommendation that fits around it.';
      }
      if (cartStatus) cartStatus.textContent = '';
      if (cartCheck) cartCheck.hidden = true;

      this.populateProductFilters();
      this.syncProductCaffeineFilterVisibility();
      this.renderProductBrowser();
      this.updateSodium();
      this.applyCartSelectionToPlan();

      const schedule = this.$('[data-schedule]');
      if (schedule) {
        schedule.replaceChildren(
          this.node(
            'li',
            p.remainingLow === 0
              ? 'No additional fuel servings are scheduled.'
              : 'Choose a fuel above to see your suggested serving timings.'
          )
        );
      }
    }

    productFit(product) {
      const cacheKey = product?.id != null ? String(product.id) : '';
      if (cacheKey && this.fitCache?.has(cacheKey)) {
        return this.fitCache.get(cacheKey);
      }

      const pack = this.engine.pack(product, this.plan, this.input);
      const target = Math.max(0, Number(this.plan?.total || 0));
      const targetLow = Math.max(0, Number(this.plan?.totalLow ?? target));
      const targetHigh = Math.max(targetLow, Number(this.plan?.totalHigh ?? target));
      const achieved = Number(pack.totalCarbs ?? (pack.carbs + (this.plan?.external || 0)));
      const rate = Number(pack.achievedRate || 0);
      const below = Math.max(0, targetLow - achieved);
      const above = Math.max(0, achieved - targetHigh);
      const rangeDistance = below + above;
      const centerDistance = Math.abs(achieved - target);

      const limits = [product.maxServings, product.maxServingsPerDay]
        .filter(value => Number.isInteger(value) && value >= 1);
      const limit = limits.length ? Math.min(...limits) : null;
      const interval = pack.servings ? this.plan.minutes / (pack.servings + 1) : 0;
      const caffeineThreshold = Number(this.plan.caffeineReviewThreshold || 200);

      const issues = [];
      const boundaryTolerance = Number(this.plan.fitBoundaryTolerance || 5);
      const carbOutside = rangeDistance > boundaryTolerance;
      const limitExceeded = limit != null && pack.servings > limit;
      const highRateSingleSource = rate > 60.01 && product.multiSource !== true;
      const caffeineReview = pack.totalCaffeine > caffeineThreshold;
      const accessConcern = this.input.access === 'no' && pack.servings > 0;
      const intervalConcern = interval > 0 && interval < 10;

      if (limitExceeded) {
        issues.push(
          `This plan would use ${pack.servings} servings, while this product lists a maximum of ${limit}.`
        );
      }
      if (carbOutside) {
        issues.push(
          below > 0
            ? `This option lands ${Math.round(below)}g below your carbohydrate planning range.`
            : `This option lands ${Math.round(above)}g above your carbohydrate planning range.`
        );
      }
      if (highRateSingleSource) {
        issues.push(
          `This option would average ${rate.toFixed(1)} g/hour but is not verified as a multiple-transportable-carbohydrate source for intake above 60 g/hour.`
        );
      }
      if (caffeineReview) {
        issues.push(
          `This option would provide ${pack.totalCaffeine}mg known caffeine, above the planner’s ${caffeineThreshold}mg review reference${this.plan.weight ? ` (~3 mg/kg at ${this.plan.weight} kg)` : ''}.`
        );
      }
      if (accessConcern) {
        issues.push('You said fuel may be difficult to access during the session.');
      }
      if (intervalConcern) {
        issues.push('The servings would need to be less than 10 minutes apart.');
      }

      const recommended = issues.length === 0;
      const fitState = rangeDistance <= boundaryTolerance ? 'close' : above > 0 ? 'over' : 'under';
      const differenceText = rangeDistance <= boundaryTolerance
        ? 'Within range'
        : `${Math.round(rangeDistance)}g ${above > 0 ? 'above range' : 'below range'}`;

      const result = {
        pack,
        target,
        targetLow,
        targetHigh,
        achieved,
        rate,
        rangeDistance,
        centerDistance,
        recommended,
        issues,
        fitState,
        differenceText
      };

      if (cacheKey) this.fitCache.set(cacheKey, result);
      return result;
    }

    fitMeter(fit) {
      const meter = this.node('div', undefined, `rf-fit-meter is-${fit.fitState}`);
      const top = this.node('div', undefined, 'rf-fit-meter-head');
      const rangeLabel = fit.targetLow === fit.targetHigh
        ? `${fit.targetHigh}g plan`
        : `${fit.targetLow}–${fit.targetHigh}g range`;
      top.append(this.node('span', rangeLabel));
      top.append(this.node('strong', fit.differenceText));
      meter.append(top);

      const track = this.node('div', undefined, 'rf-fit-meter-track');
      track.setAttribute(
        'aria-label',
        `${Math.round(fit.achieved)} grams planned, ${fit.rate.toFixed(1)} grams per hour. Planning range ${fit.targetLow} to ${fit.targetHigh} grams; starting aim ${fit.target} grams. ${fit.differenceText}.`
      );
      track.setAttribute('role', 'img');

      const scale = Math.max(1, fit.targetHigh * 1.25, fit.achieved * 1.1, fit.target * 1.15);
      const fillPercent = Math.max(0, Math.min(100, (fit.achieved / scale) * 100));
      const rangeStartPercent = Math.max(0, Math.min(100, (fit.targetLow / scale) * 100));
      const rangeEndPercent = Math.max(rangeStartPercent, Math.min(100, (fit.targetHigh / scale) * 100));
      const targetPercent = Math.max(0, Math.min(100, (fit.target / scale) * 100));

      const rangeBand = this.node('span', undefined, 'rf-fit-meter-range');
      rangeBand.style.left = `${rangeStartPercent}%`;
      rangeBand.style.width = `${Math.max(1, rangeEndPercent - rangeStartPercent)}%`;
      const fill = this.node('span', undefined, 'rf-fit-meter-fill');
      fill.style.width = `${fillPercent}%`;
      const marker = this.node('span', undefined, 'rf-fit-meter-target');
      marker.style.left = `${targetPercent}%`;
      track.append(rangeBand, fill, marker);
      meter.append(track);

      const bottom = this.node('div', undefined, 'rf-fit-meter-foot');
      bottom.append(this.node('span', `${Math.round(fit.achieved)}g planned · ${fit.rate.toFixed(1)}g/h`));
      bottom.append(this.node('span', `Aim ${fit.target}g`));
      meter.append(bottom);
      return meter;
    }

    cardTargetMeter({
      label,
      tone,
      achieved,
      rate = null,
      targetLow,
      targetHigh,
      target = null,
      unit,
      ariaDetail = ''
    }) {
      const safeAchieved = Math.max(0, Number(achieved || 0));
      const safeLow = Math.max(0, Number(targetLow || 0));
      const safeHigh = Math.max(safeLow, Number(targetHigh ?? safeLow));
      const safeTarget = Math.max(safeLow, Math.min(safeHigh || safeLow, Number(target ?? ((safeLow + safeHigh) / 2))));
      const scale = Math.max(1, safeHigh * 1.22, safeAchieved * 1.08, safeTarget * 1.12);

      const meter = this.node('div', undefined, `rf-card-target-meter is-${tone}`);
      const head = this.node('div', undefined, 'rf-card-target-meter-head');

      const left = this.node('div', undefined, 'rf-card-target-meter-value');
      left.append(this.node('strong', label));
      const achievedText = unit === 'g'
        ? `${Math.round(safeAchieved)}${unit}${rate != null ? ` · ${Number(rate).toFixed(1)}g/h` : ''}`
        : `${Math.round(safeAchieved)} ${unit}`;
      left.append(this.node('span', achievedText));

      const rangeText = safeLow === safeHigh
        ? `${Math.round(safeHigh)} ${unit} target`
        : `${Math.round(safeLow)}–${Math.round(safeHigh)} ${unit}`;
      head.append(left, this.node('span', rangeText, 'rf-card-target-meter-range-label'));
      meter.append(head);

      const track = this.node('div', undefined, 'rf-card-target-meter-track');
      const fillPercent = Math.max(0, Math.min(100, (safeAchieved / scale) * 100));
      const rangeStartPercent = Math.max(0, Math.min(100, (safeLow / scale) * 100));
      const rangeEndPercent = Math.max(rangeStartPercent, Math.min(100, (safeHigh / scale) * 100));
      const targetPercent = Math.max(0, Math.min(100, (safeTarget / scale) * 100));

      const rangeBand = this.node('span', undefined, 'rf-card-target-meter-band');
      rangeBand.style.left = `${rangeStartPercent}%`;
      rangeBand.style.width = `${Math.max(1, rangeEndPercent - rangeStartPercent)}%`;

      const fill = this.node('span', undefined, 'rf-card-target-meter-fill');
      fill.style.width = `${fillPercent}%`;

      const marker = this.node('span', undefined, 'rf-card-target-meter-marker');
      marker.style.left = `${targetPercent}%`;

      track.append(rangeBand, fill, marker);
      track.setAttribute('role', 'img');
      track.setAttribute(
        'aria-label',
        `${label}: ${Math.round(safeAchieved)} ${unit} planned. Target range ${Math.round(safeLow)} to ${Math.round(safeHigh)} ${unit}.${ariaDetail ? ` ${ariaDetail}` : ''}`
      );
      meter.append(track);

      return meter;
    }

    cardDualTargetMeters(fit) {
      const wrap = this.node('div', undefined, 'rf-card-dual-targets');

      wrap.append(this.cardTargetMeter({
        label: 'CARBS',
        tone: 'carbs',
        achieved: fit.achieved,
        rate: fit.rate,
        targetLow: fit.targetLow,
        targetHigh: fit.targetHigh,
        target: fit.target,
        unit: 'g',
        ariaDetail: fit.differenceText
      }));

      const sodiumRange = Array.isArray(this.plan?.sodiumTotalRange)
        ? this.plan.sodiumTotalRange
        : [Number(this.plan?.sodiumTotal || 0), Number(this.plan?.sodiumTotal || 0)];
      const externalSodium = Math.max(0, Number(this.plan?.externalSodium || 0));
      const fuelSodium = Math.max(0, Number(fit.pack?.sodium || 0));
      const combinedSodium = externalSodium + fuelSodium;

      wrap.append(this.cardTargetMeter({
        label: 'SODIUM',
        tone: 'sodium',
        achieved: combinedSodium,
        targetLow: sodiumRange[0],
        targetHigh: sodiumRange[1],
        target: Number(this.plan?.sodiumTotal || ((Number(sodiumRange[0]) + Number(sodiumRange[1])) / 2)),
        unit: 'mg',
        ariaDetail: externalSodium > 0
          ? `This includes ${Math.round(fuelSodium)} mg from this fuel and ${Math.round(externalSodium)} mg already planned from other sources. Electrolyte selection can add more sodium later.`
          : `This fuel contributes ${Math.round(fuelSodium)} mg. Electrolyte selection can add more sodium later.`
      }));

      return wrap;
    }

    productScore(product) {
      const fit = this.productFit(product);
      const ranking = this.engine.fuelRecommendationScore(
        fit.pack,
        this.plan,
        this.input,
        fit.recommended
      );

      let score = ranking.score;

      // This should normally already be filtered by eligibility, but retain
      // the explicit protection if product data changes upstream.
      if (this.input.caffeine !== 'yes' && product.caffeine > 0) score += 100000;

      return score;
    }

    syncProductCaffeineFilterVisibility() {
      const control = this.$('[data-product-caffeine]');
      if (!control) return;

      const wrapper =
        control.closest('[data-product-filter-caffeine]') ||
        control.closest('.rf-product-filter') ||
        control.closest('label') ||
        control.parentElement;

      const caffeineAllowed = this.input?.caffeine === 'yes';

      if (!caffeineAllowed) {
        // The questionnaire already enforces caffeine-free exact variants.
        // Hide this redundant secondary filter and clear stale UI state.
        this.productFilters.caffeine = 'all';
        control.value = 'all';
        control.disabled = true;
        if (wrapper) wrapper.hidden = true;
        return;
      }

      control.disabled = false;
      if (wrapper) wrapper.hidden = false;

      // Restore the current live filter only when caffeine is allowed.
      control.value = this.productFilters.caffeine || 'all';
    }

    populateProductFilters() {
      const brandSelect = this.$('[data-product-brand]');
      if (!brandSelect) return;

      const current = this.productFilters.brand;
      const labels = new Map();
      this.productPool.forEach(p => labels.set(p.brand, this.displayBrand(p)));
      const brands = [...labels.entries()].sort((a, b) => a[1].localeCompare(b[1]));

      brandSelect.replaceChildren();
      const all = this.node('option', 'All brands');
      all.value = 'all';
      brandSelect.append(all);
      brands.forEach(([value, label]) => {
        const option = this.node('option', label);
        option.value = value;
        brandSelect.append(option);
      });

      brandSelect.value = brands.some(([value]) => value === current) ? current : 'all';
      this.productFilters.brand = brandSelect.value;
      const caffeineSelect = this.$('[data-product-caffeine]');
      const sortSelect = this.$('[data-product-sort]');
      if (caffeineSelect) caffeineSelect.value = this.productFilters.caffeine;
      if (sortSelect) sortSelect.value = this.productFilters.sort;

      this.syncProductCaffeineFilterVisibility();
    }

    filteredProducts() {
      let list = [...this.productPool].filter(Boolean);
      const f = this.productFilters;

      if (f.format !== 'all') list = list.filter(p => p.format === f.format);
      if (f.brand !== 'all') list = list.filter(p => p.brand === f.brand);
      if (f.caffeine === 'free') list = list.filter(p => p.caffeine === 0);
      if (f.caffeine === 'caffeinated') list = list.filter(p => p.caffeine > 0);

      list.sort((a, b) => {
        const fa = this.productFit(a);
        const fb = this.productFit(b);
        const pa = fa.pack;
        const pb = fb.pack;

        if (f.sort === 'lowest-cost') {
          return pa.price - pb.price || this.productScore(a) - this.productScore(b);
        }
        if (f.sort === 'fewest-packs') {
          return pa.units - pb.units || pa.price - pb.price;
        }
        if (f.sort === 'highest-carbs') {
          return b.carbs - a.carbs || this.productScore(a) - this.productScore(b);
        }
        return this.productScore(a) - this.productScore(b);
      });

      return list;
    }

    flavourOptionIndexes(product) {
      const names = Array.isArray(product?.optionNames) ? product.optionNames : [];
      const indexes = [];
      names.forEach((name, index) => {
        if (/flavou?r|taste/i.test(String(name || ''))) indexes.push(index);
      });
      return indexes;
    }

    packOptionIndexes(product) {
      const names = Array.isArray(product?.optionNames) ? product.optionNames : [];
      const indexes = [];
      names.forEach((name, index) => {
        if (/pack|quantity|qty|count|servings?|size/i.test(String(name || ''))) indexes.push(index);
      });
      return indexes;
    }

    familyKey(product) {
      if (!product) return '';
      const productIdentity = String(product.productId || product.handle || '');
      const values = Array.isArray(product.optionValues) ? product.optionValues : [];
      const flavourIndexes = this.flavourOptionIndexes(product);
      const packIndexes = this.packOptionIndexes(product);
      const ignored = new Set([...flavourIndexes, ...packIndexes]);
      const hasRecognisedOptions = ignored.size > 0;

      // Only preserve non-flavour/non-pack option values when Shopify exposes
      // meaningful option names. If the catalogue only exposes generic titles,
      // rely on product identity + formulation nutrition and parse flavour/pack
      // from the variant title inside the modal.
      const definingOptions = hasRecognisedOptions
        ? values
            .filter((_, index) => !ignored.has(index))
            .map(value => String(value || '').trim().toLowerCase())
            .filter(Boolean)
        : [];

      // Do NOT include exact nutrition values in the family key.
      // Flavour labels can legitimately vary slightly in carbohydrate/sodium
      // while still belonging to the same shopper-facing product family.
      // Material formulation differences stay separated by their non-pack /
      // non-flavour Shopify option values.
      return [
        productIdentity,
        product.role,
        product.format,
        ...definingOptions
      ].join('|');
    }

    familyMembers(product, source = this.productPool) {
      const key = this.familyKey(product);
      return (source || []).filter(candidate => candidate && this.familyKey(candidate) === key);
    }

    modalVariantAllowed(product) {
      if (!product || !this.engine.validProduct(product)) return false;
      if (product.role !== 'fuel') return false;

      // Preserve questionnaire-level caffeine preference/safety.
      if (this.input?.caffeine !== 'yes' && Number(product.caffeine) !== 0) return false;

      // Also apply the live product-browser caffeine filter at exact-variant
      // level. Mixed families (e.g. GU Original) must not keep caffeinated
      // flavours inside the modal when the customer selects Caffeine-free.
      const caffeineFilter = String(this.productFilters?.caffeine || 'all');
      if (caffeineFilter === 'free' && Number(product.caffeine) !== 0) return false;
      if (caffeineFilter === 'caffeinated' && Number(product.caffeine) <= 0) return false;

      if (this.input?.diet === 'vegan' && product.vegan !== true) return false;
      if (this.input?.diet === 'gluten' && product.glutenFree !== true) return false;
      if (this.input?.diet === 'dairy' && product.dairyFree !== true) return false;

      if (this.input?.water !== 'yes' && product.requiresWater !== false) return false;

      return true;
    }

    familyModalMembers(product) {
      const key = this.familyKey(product);
      return (this.products || []).filter(candidate =>
        candidate &&
        this.familyKey(candidate) === key &&
        this.modalVariantAllowed(candidate)
      );
    }

    variantParts(product) {
      return String(product?.variant || '')
        .split(/\s*(?:\/|\||•)\s*/)
        .map(part => part.trim())
        .filter(Boolean);
    }

    looksLikePackPart(part, product) {
      const text = String(part || '').trim().toLowerCase();
      const servings = Number(product?.servings || 0);
      if (!text) return false;
      if (servings > 0 && new RegExp(`^${servings}(?:\\s*(?:pack|packs|pouches|sachets|servings?|pcs?|pieces?))?$`, 'i').test(text)) return true;
      return /^\d+\s*(?:pack|packs|pouches|sachets|servings?|pcs?|pieces?)$/i.test(text);
    }

    flavourLabel(product) {
      const values = Array.isArray(product?.optionValues) ? product.optionValues : [];
      const flavourIndexes = this.flavourOptionIndexes(product);
      const explicit = flavourIndexes
        .map(index => values[index])
        .filter(Boolean)
        .join(' / ');
      if (explicit) return explicit;

      const fallback = this.variantParts(product)
        .filter(part => !this.looksLikePackPart(part, product))
        .filter(part => !/^\d+(?:\.\d+)?$/.test(part))
        .join(' / ');

      return fallback || 'Standard';
    }

    packLabel(product) {
      const values = Array.isArray(product?.optionValues) ? product.optionValues : [];
      const packIndexes = this.packOptionIndexes(product);
      const explicit = packIndexes
        .map(index => values[index])
        .filter(Boolean)
        .join(' / ');
      if (explicit) return explicit;

      const parts = this.variantParts(product);
      const matched = parts.find(part => this.looksLikePackPart(part, product));
      if (matched) return matched;

      const servings = Number(product?.servings || 0);
      return servings > 0
        ? `${servings} serving${servings === 1 ? '' : 's'} / pack`
        : 'Standard pack';
    }

    familyFlavourCount(members) {
      return new Set((members || []).map(product => this.flavourLabel(product))).size;
    }

    familyPackCount(members) {
      return new Set((members || []).map(product => `${this.packLabel(product)}|${product.servings}`)).size;
    }

    familyCartItems(familyKey) {
      return (this.cartSelection?.items || []).filter(item => {
        if (item.familyKey) return String(item.familyKey) === String(familyKey);
        const product = this.productPool.find(candidate => String(candidate.id) === String(item.id));
        return product && this.familyKey(product) === familyKey;
      });
    }

    familySelectionNeedsEditing(familyKey) {
      const items = this.familyCartItems(familyKey);
      if (!items.length) return false;

      // Complex family selections should reopen the modal for editing.
      // Only a single exact Shopify variant at quantity 1 is safe for
      // immediate one-click removal.
      if (items.length > 1) return true;

      const totalQuantity = items.reduce(
        (sum, item) => sum + Math.max(0, Number(item.quantity || 0)),
        0
      );
      return totalQuantity > 1;
    }

    selectedPlannerItemCount() {
      return new Set(
        (this.cartSelection?.items || [])
          .map(item => String(item.familyKey || ''))
          .filter(Boolean)
      ).size;
    }

    metricRange(members, key, suffix) {
      const values = [...new Set((members || [])
        .map(product => Number(product?.[key]))
        .filter(Number.isFinite))]
        .sort((a, b) => a - b);
      if (!values.length) return '—';
      if (values.length === 1) return `${values[0]}${suffix}`;
      return `${values[0]}–${values[values.length - 1]}${suffix}`;
    }

    productFamilies() {
      const map = new Map();

      // Card-level filtering still starts with the plan-filtered products.
      // Mixed-caffeine families remain as a single card when at least one exact
      // variant matches the active product filter.
      this.filteredProducts().forEach(product => {
        const key = this.familyKey(product);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(product);
      });

      return [...map.entries()]
        .map(([key, members]) => {
          const modalMembers = this.familyModalMembers(members[0]);
          if (!modalMembers.length) return null;

          const representativeCandidates = members.filter(member =>
            modalMembers.some(option => String(option.id) === String(member.id))
          );
          const candidates = representativeCandidates.length ? representativeCandidates : modalMembers;
          const representative = [...candidates].sort((a, b) => this.productScore(a) - this.productScore(b))[0];

          return { key, members, representative };
        })
        .filter(Boolean);
    }

    familySelected(family) {
      if (!family) return false;
      if (this.familyCartItems(family.key).length) return true;
      const selectedIds = new Set((this.cartSelection?.items || []).map(item => String(item.id)));
      return family.members.some(product => selectedIds.has(String(product.id)));
    }

    productCard(family, index, alternative = false) {
      const product = family.representative;
      const members = family.members;
      const modalMembers = this.familyModalMembers(product);
      const displayMembers = modalMembers.length ? modalMembers : members;
      const fit = this.productFit(product);
      const pack = fit.pack;
      const selected = this.familySelected(family);

      const card = this.node('article', undefined, 'rf-product-card');
      card.dataset.productId = String(product.id);
      card.dataset.familyKey = family.key;
      if (selected) card.classList.add('is-selected');
      if (alternative) card.classList.add('is-alternative');

      const media = this.node('div', undefined, 'rf-product-card-media');
      if (product.image) {
        const img = this.node('img');
        img.src = product.image;
        img.alt = this.displayTitle(product);
        img.loading = 'lazy';
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.width = 420;
        img.height = 240;
        media.append(img);
      }

      const badgeText = alternative
        ? 'ALTERNATIVE'
        : index === 0 && this.productFilters.sort === 'recommended'
          ? 'TOP MATCH'
          : 'RECOMMENDED';
      const badge = this.node('span', badgeText, 'rf-product-badge');
      if (alternative) badge.classList.add('is-alternative');
      media.append(badge);
      card.append(media);

      const body = this.node('div', undefined, 'rf-product-card-body');
      body.append(this.node('p', this.displayBrand(product), 'rf-product-card-brand'));
      body.append(this.node('h3', this.displayTitle(product), 'rf-product-card-title'));
      const flavourCount = this.familyFlavourCount(displayMembers);
      const packCount = this.familyPackCount(displayMembers);
      const optionParts = [];
      if (flavourCount > 1) optionParts.push(`${flavourCount} flavours`);
      if (packCount > 1) optionParts.push(`${packCount} pack options`);
      if (!optionParts.length) optionParts.push(this.packLabel(product));
      body.append(this.node('p', optionParts.join(' · '), 'rf-product-card-variant'));
      body.append(this.node(
        'p',
        `${this.metricRange(members, 'carbs', 'g carbs')} · ` +
        `${this.metricRange(members, 'caffeine', 'mg caffeine')} · ` +
        `${this.metricRange(members, 'sodium', 'mg sodium')} per serving`,
        'rf-product-card-nutrition'
      ));

      const planBox = this.node('div', undefined, 'rf-product-card-plan');
      planBox.append(this.node('strong', 'YOUR PLAN'));
      planBox.append(this.node(
        'span',
        `Use ${pack.servings} serving${pack.servings === 1 ? '' : 's'} · ${Math.round(pack.carbs)}g carbs · ${Math.round(pack.sodium)}mg sodium`
      ));
      planBox.append(this.cardDualTargetMeters(fit));

      if (alternative && fit.issues.length) {
        planBox.append(this.node('span', `Why alternative: ${fit.issues[0]}`, 'rf-plan-alert'));
      }

      if (pack.times?.length) {
        const timing = pack.times
          .map(time => `${Math.floor(time / 60)}:${String(time % 60).padStart(2, '0')}`)
          .join(' · ');
        planBox.append(this.node('span', `Timing: ${timing}`));
      }

      const purchaseLine = this.purchaseCopy(pack, product);
      planBox.append(this.node(
        'span',
        pack.leftover > 0 ? `${purchaseLine} · ${pack.leftover} left for training` : purchaseLine
      ));
      body.append(planBox);

      const actions = this.node('div', undefined, 'rf-product-card-actions');
      const memberPrices = members.map(member => this.productFit(member).pack.price).filter(Number.isFinite);
      const minPrice = memberPrices.length ? Math.min(...memberPrices) : pack.price || 0;
      actions.append(this.node('strong', `${members.length > 1 ? 'From ' : ''}${this.money(minPrice)}`, 'rf-product-card-price'));

      if (!this.config.previewMode && product.url) {
        const link = this.node('a', 'VIEW DETAILS →', 'rf-product-detail-link');
        link.href = product.url;
        actions.append(link);
      }
      body.append(actions);

      const selectedFamilyNeedsEditing = selected && this.familySelectionNeedsEditing(family.key);
      const button = this.node(
        'button',
        selected
          ? selectedFamilyNeedsEditing
            ? 'EDIT SELECTION →'
            : 'SELECTED · REMOVE'
          : alternative
            ? 'SELECT ALTERNATIVE →'
            : (this.familyFlavourCount(displayMembers) > 1 || this.familyPackCount(displayMembers) > 1)
              ? 'CHOOSE OPTIONS →'
              : 'SELECT THIS FUEL →',
        'rf-button rf-product-select-button'
      );
      button.type = 'button';
      button.dataset.action = 'select-product';
      button.dataset.productId = String(product.id);
      body.append(button);

      card.append(body);
      return card;
    }

    productGroup(title, description, families, alternative = false) {
      const group = this.node('section', undefined, `rf-product-group${alternative ? ' is-alternative' : ''}`);
      const head = this.node('div', undefined, 'rf-product-group-head');
      head.append(this.node('h3', title));
      if (description) head.append(this.node('p', description));
      group.append(head);

      const cards = this.node('div', undefined, 'rf-product-card-grid');
      families.forEach((family, index) => cards.append(this.productCard(family, index, alternative)));
      group.append(cards);
      return group;
    }

    renderProductBrowser() {
      const browser = this.$('[data-product-browser]');
      const grid = this.$('[data-product-grid]');
      if (!browser || !grid) return;

      browser.hidden = !this.plan;
      if (!this.plan) {
        grid.replaceChildren();
        return;
      }

      const count = this.$('[data-product-count]');
      const empty = this.$('[data-product-empty]');
      const show = this.$('[data-show-products]');

      if (!this.productPool.length) {
        grid.replaceChildren();
        if (count) count.textContent = '0 matches';
        if (empty) {
          empty.hidden = false;
          empty.textContent = this.plan.remainingLow === 0
            ? 'No extra during-session fuel is needed because your already-planned carbohydrate reaches the planning range.'
            : 'No available fuel currently matches these preferences. Try adjusting a filter or your answers.';
        }
        if (show) show.hidden = true;
        return;
      }

      const families = this.productFamilies();
      const recommended = families.filter(family => this.productFit(family.representative).recommended);
      const alternatives = families.filter(family => !this.productFit(family.representative).recommended);
      const visibleRecommended = this.showAllProducts ? recommended : recommended.slice(0, 4);
      const visibleAlternatives = this.showAllProducts ? alternatives : [];

      if (count) {
        const selectedFamilies = new Set(
          (this.cartSelection?.items || [])
            .map(item => item.familyKey)
            .filter(Boolean)
        ).size;
        count.textContent = `${recommended.length} product match${recommended.length === 1 ? '' : 'es'}` +
          (alternatives.length ? ` · ${alternatives.length} alternative${alternatives.length === 1 ? '' : 's'}` : '') +
          (selectedFamilies ? ` · ${selectedFamilies} selected` : '');
      }

      if (empty) {
        empty.hidden = families.length > 0;
        empty.textContent = families.length
          ? ''
          : 'No products match these filters. Try All formats, All brands, or All allowed caffeine options.';
      }

      grid.replaceChildren();
      if (visibleRecommended.length) {
        grid.append(this.productGroup(
          'TOP MATCHES',
          'One card per product family. Choose pack size and flavour after selecting.',
          visibleRecommended,
          false
        ));
      }
      if (visibleAlternatives.length) {
        grid.append(this.productGroup(
          'ALTERNATIVES',
          'These sit outside the carbohydrate range or trigger another review flag. You can still choose exact flavours.',
          visibleAlternatives,
          true
        ));
      }

      const hiddenCount = Math.max(0, recommended.length - visibleRecommended.length) +
        Math.max(0, alternatives.length - visibleAlternatives.length);
      if (show) {
        show.hidden = this.showAllProducts ? families.length <= 4 : hiddenCount === 0;
        show.textContent = this.showAllProducts
          ? 'SHOW TOP 4 ↑'
          : `SHOW MORE OPTIONS (${hiddenCount}) ↓`;
      }

      this.querySelectorAll('[data-action="filter-format"]').forEach(button => {
        const active = button.dataset.productFormat === this.productFilters.format;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    selectProduct(id) {
      const product = this.productPool.find(candidate => candidate && String(candidate.id) === String(id));
      if (!product) {
        this.showToast('This product option is no longer available.');
        return;
      }

      const members = this.familyModalMembers(product);
      const family = { key: this.familyKey(product), representative: product, members };
      const selected = this.familySelected(family);

      if (selected && !this.familySelectionNeedsEditing(family.key)) {
        this.removeFuelSelection(family.key);
        return;
      }

      try {
        this.openFuelModal(product, { editing: selected });
      } catch (error) {
        console.error('[Racepack Fuel Planner] Could not open fuel modal', error);
        const reason = error && error.message ? ` (${error.message})` : '';
        this.showToast(`Could not open product options${reason}`);
      }
    }

    ensureFuelModal() {
      let modal = this.$('[data-fuel-modal]');
      if (modal && this.$('[data-fuel-modal-rows]')) return modal;

      // Self-healing fallback:
      // Shopify/theme caching or an older section version must never make the
      // product buttons unusable. If the Liquid modal is absent, build the
      // complete modal inside this planner instance at runtime.
      modal = this.node('div', undefined, 'rf-modal-backdrop');
      modal.dataset.fuelModal = '';
      modal.hidden = true;

      const panel = this.node('section', undefined, 'rf-fuel-modal');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');

      const head = this.node('div', undefined, 'rf-fuel-modal-head');
      const headingWrap = this.node('div');
      headingWrap.append(this.node('p', 'CHOOSE PACKS & FLAVOURS', 'rf-modal-kicker'));

      const heading = this.node('h3', 'Fuel selection');
      heading.dataset.fuelModalTitle = '';
      const headingId = `rf-fuel-modal-title-${Math.random().toString(36).slice(2, 9)}`;
      heading.id = headingId;
      panel.setAttribute('aria-labelledby', headingId);
      headingWrap.append(heading);

      const close = this.node('button', '×', 'rf-modal-close');
      close.type = 'button';
      close.dataset.action = 'close-fuel-modal';
      close.setAttribute('aria-label', 'Close');
      head.append(headingWrap, close);
      panel.append(head);

      const intro = this.node('p');
      intro.dataset.fuelModalIntro = '';
      panel.append(intro);

      const plan = this.node('div', undefined, 'rf-modal-plan');
      plan.append(this.node('strong', 'THIS SESSION'));
      const target = this.node('p');
      target.dataset.fuelModalTarget = '';
      plan.append(target);
      panel.append(plan);

      panel.append(this.node(
        'div',
        'Choose one flavour or mix several. Buying extra packs is allowed — extras are treated as stock for other training sessions or races. They do not increase this session’s recommended carbohydrate range.',
        'rf-modal-note'
      ));

      const rows = this.node('div', undefined, 'rf-modal-flavours');
      rows.dataset.fuelModalRows = '';
      panel.append(rows);

      const foot = this.node('div', undefined, 'rf-fuel-modal-foot');
      const summary = this.node('strong', 'Choose at least one pack.');
      summary.dataset.fuelModalSummary = '';

      const confirm = this.node('button', 'ADD TO CART →', 'rf-button');
      confirm.type = 'button';
      confirm.dataset.action = 'confirm-fuel-modal';
      const removeFamily = this.node('button', 'REMOVE THIS PRODUCT', 'rf-button rf-button-outline');
      removeFamily.type = 'button';
      removeFamily.dataset.action = 'remove-modal-family';
      removeFamily.hidden = true;

      foot.append(summary, removeFamily, confirm);
      panel.append(foot);

      const error = this.node('p', '', 'rf-modal-error');
      error.dataset.fuelModalError = '';
      error.setAttribute('role', 'alert');
      error.hidden = true;
      panel.append(error);

      modal.append(panel);
      this.append(modal);
      return modal;
    }

    modalGroups(members) {
      const groups = new Map();
      (members || []).forEach(product => {
        const label = this.flavourLabel(product);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(product);
      });

      return [...groups.entries()].map(([label, variants]) => ({
        label,
        variants: [...variants].sort((a, b) => {
          const sa = Number(a.servings || 0);
          const sb = Number(b.servings || 0);
          return sa - sb || Number(a.price || 0) - Number(b.price || 0);
        })
      }));
    }

    modalRowProduct(row) {
      if (!row) return null;
      return row.variants.find(product => String(product.id) === String(row.selectedVariantId)) || row.variants[0] || null;
    }

    openFuelModal(product, { editing = false } = {}) {
      const modal = this.ensureFuelModal();
      const rows = this.$('[data-fuel-modal-rows]');
      if (!modal || !rows) {
        this.showToast('Product selector could not load.');
        return;
      }

      const familyKey = this.familyKey(product);
      const siblings = this.familyModalMembers(product);
      if (!siblings.length) throw new Error('No currently available exact variants were found for this product family.');

      this.modalProduct = product;
      this.modalFit = this.productFit(product);
      this.modalFamilyKey = familyKey;
      this.modalEditing = Boolean(editing);
      this.lastFocusedElement = document.activeElement;

      const title = this.$('[data-fuel-modal-title]');
      const intro = this.$('[data-fuel-modal-intro]');
      const target = this.$('[data-fuel-modal-target]');
      const errorNode = this.$('[data-fuel-modal-error]');
      const confirm = this.$('[data-action="confirm-fuel-modal"]');
      const removeFamily = this.$('[data-action="remove-modal-family"]');

      if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
      if (title) title.textContent = this.displayTitle(product);
      if (intro) {
        intro.textContent =
          `${this.displayBrand(product)} · ${this.familyFlavourCount(siblings)} flavour option${this.familyFlavourCount(siblings) === 1 ? '' : 's'} · ` +
          `${this.familyPackCount(siblings)} pack option${this.familyPackCount(siblings) === 1 ? '' : 's'}.`;
      }

      if (target) {
        const fit = this.modalFit || {};
        const pack = fit.pack || {};
        const servings = Number.isFinite(Number(pack.servings)) ? Number(pack.servings) : 1;
        const achieved = Number.isFinite(Number(fit.achieved)) ? Math.round(Number(fit.achieved)) : Number(product.carbs || 0);
        const rate = Number.isFinite(Number(fit.rate)) ? Number(fit.rate).toFixed(1) : '—';
        target.textContent =
          `For this session: ${servings} serving${servings === 1 ? '' : 's'} planned ` +
          `(${achieved}g carbohydrate, ${rate}g/h). Pack/flavour quantities below are shopping choices and do not change this target.`;
      }

      const existing = new Map(
        this.familyCartItems(familyKey).map(item => [String(item.id), Number(item.quantity || 0)])
      );

      const groups = this.modalGroups(siblings);
      this.modalRows = groups.map(group => {
        let selected = group.variants.find(variant => existing.has(String(variant.id))) || null;
        let quantity = selected ? Number(existing.get(String(selected.id)) || 0) : 0;

        if (!selected) {
          selected = group.variants.find(variant => String(variant.id) === String(product.id)) || group.variants[0];
        }

        // New selection: start only the clicked/reference row at its recommended purchase quantity.
        if (!editing && String(selected.id) === String(product.id)) {
          quantity = Math.max(1, Number(this.productFit(selected)?.pack?.units || 1));
        }

        return {
          label: group.label,
          variants: group.variants,
          selectedVariantId: selected.id,
          quantity
        };
      });

      rows.replaceChildren();
      this.modalRows.forEach((row, index) => {
        const wrap = this.node('div', undefined, 'rf-modal-flavour-row');

        const info = this.node('div', undefined, 'rf-modal-flavour-info');
        info.append(this.node('strong', row.label));

        const packSelect = this.node('select', undefined, 'rf-modal-pack-select');
        packSelect.dataset.modalPack = String(index);
        packSelect.setAttribute('aria-label', `${row.label} pack size`);

        row.variants.forEach(variant => {
          const option = this.node(
            'option',
            `${this.packLabel(variant)} · ${this.money(variant.price)}`
          );
          option.value = String(variant.id);
          option.selected = String(variant.id) === String(row.selectedVariantId);
          packSelect.append(option);
        });

        packSelect.addEventListener('change', () => {
          this.changeModalPack(index, packSelect.value);
        }, { signal: this.abort.signal });

        info.append(packSelect);

        const nutrition = this.node('span');
        nutrition.dataset.modalNutrition = String(index);
        info.append(nutrition);
        wrap.append(info);

        const stepper = this.node('div', undefined, 'rf-modal-stepper');
        const minus = this.node('button', '−', 'rf-modal-step');
        minus.type = 'button';
        minus.dataset.action = 'modal-qty';
        minus.dataset.index = String(index);
        minus.dataset.delta = '-1';
        minus.setAttribute('aria-label', `Reduce ${row.label}`);

        const qty = this.node('output', String(row.quantity), 'rf-modal-qty');
        qty.dataset.modalQty = String(index);
        qty.setAttribute('aria-live', 'polite');

        const plus = this.node('button', '+', 'rf-modal-step');
        plus.type = 'button';
        plus.dataset.action = 'modal-qty';
        plus.dataset.index = String(index);
        plus.dataset.delta = '1';
        plus.setAttribute('aria-label', `Add ${row.label}`);

        stepper.append(minus, qty, plus);
        wrap.append(stepper);
        rows.append(wrap);

        this.paintModalRow(index);
      });

      if (confirm) confirm.textContent = editing ? 'SAVE CHANGES →' : 'ADD TO CART →';
      if (removeFamily) removeFamily.hidden = !editing;

      this.updateFuelModalSummary();
      modal.hidden = false;
      document.documentElement.classList.add('rf-modal-open');
      document.body.classList.add('rf-modal-open');
      const close = this.$('[data-action="close-fuel-modal"]');
      if (close) close.focus({ preventScroll: true });
    }

    paintModalRow(index) {
      const row = this.modalRows[Number(index)];
      const product = this.modalRowProduct(row);
      if (!row || !product) return;

      const nutrition = this.querySelector(`[data-modal-nutrition="${index}"]`);
      if (nutrition) {
        nutrition.textContent =
          `${product.carbs}g carbs · ${product.caffeine}mg caffeine · ${product.sodium}mg sodium per serving · ` +
          `${product.servings} serving${product.servings === 1 ? '' : 's'} per pack`;
      }
    }

    changeModalPack(index, variantId) {
      const row = this.modalRows[Number(index)];
      if (!row) return;
      const product = row.variants.find(variant => String(variant.id) === String(variantId));
      if (!product) return;
      row.selectedVariantId = product.id;
      this.paintModalRow(index);
      this.updateFuelModalSummary();
    }

    updateFuelModalSummary() {
      const chosen = this.modalRows
        .map(row => ({ row, product: this.modalRowProduct(row) }))
        .filter(entry => entry.product && Number(entry.row.quantity || 0) > 0);

      const totalPacks = chosen.reduce((sum, entry) => sum + Number(entry.row.quantity || 0), 0);
      const choiceCount = chosen.length;
      const total = chosen.reduce(
        (sum, entry) => sum + Number(entry.row.quantity || 0) * Number(entry.product.price || 0),
        0
      );

      const summary = this.$('[data-fuel-modal-summary]');
      const confirm = this.$('[data-action="confirm-fuel-modal"]');

      if (summary) {
        summary.textContent = totalPacks
          ? `${totalPacks} pack${totalPacks === 1 ? '' : 's'} across ${choiceCount} option${choiceCount === 1 ? '' : 's'} · ${this.money(total)}`
          : this.modalEditing
            ? 'No packs selected. Use REMOVE THIS PRODUCT to remove it from your cart.'
            : 'Choose at least one pack.';
      }
      if (confirm) confirm.disabled = this.busy || totalPacks < 1;
    }

    changeModalQuantity(index, delta) {
      const row = this.modalRows[Number(index)];
      if (!row) return;
      row.quantity = Math.max(0, Math.min(24, Number(row.quantity || 0) + Number(delta || 0)));
      const out = this.querySelector(`[data-modal-qty="${index}"]`);
      if (out) out.textContent = String(row.quantity);
      this.updateFuelModalSummary();
    }

    closeFuelModal() {
      const modal = this.$('[data-fuel-modal]');
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.classList.remove('rf-modal-open');
      document.body.classList.remove('rf-modal-open');
      this.modalRows = [];
      this.modalProduct = null;
      this.modalFit = null;
      this.modalFamilyKey = '';
      this.modalEditing = false;
      if (this.lastFocusedElement?.focus) this.lastFocusedElement.focus({ preventScroll: true });
    }

    plannerSelectionToken() {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || this.config.root || '/';
      const response = await fetch(root + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Could not read the current cart.');
      return response.json();
    }

    cartSectionsRequest() {
      const sections = new Set(['cart-icon-bubble', 'cart-drawer']);
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          drawer.getSectionsToRender().forEach(section => {
            const id = section?.section || section?.id;
            if (id) sections.add(String(id));
          });
        } catch (_) {}
      }

      return [...sections];
    }

    sectionInnerHTML(html, selector = '.shopify-section') {
      if (!html) return '';
      try {
        const doc = new DOMParser().parseFromString(String(html), 'text/html');
        const target = doc.querySelector(selector);
        return target ? target.innerHTML : '';
      } catch (_) {
        return '';
      }
    }

    refreshCartSections(sections = {}, cartState = null) {
      if (!sections || typeof sections !== 'object') return;

      // Header bubble: preserve the existing anchor/listeners and only replace
      // its contents, matching Dawn's own section-rendering pattern.
      const iconHtml = sections['cart-icon-bubble'];
      const iconTarget = document.querySelector('#cart-icon-bubble');
      if (iconHtml && iconTarget) {
        const inner = this.sectionInnerHTML(iconHtml, '.shopify-section');
        if (inner) iconTarget.innerHTML = inner;
      }

      const drawer = document.querySelector('cart-drawer');
      const drawerHtml = sections['cart-drawer'];

      if (drawer) {
        if (cartState && Number.isFinite(Number(cartState.item_count))) {
          drawer.classList.toggle('is-empty', Number(cartState.item_count) === 0);
        }

        let renderedViaDawnContract = false
;

        // Dawn exposes getSectionsToRender() + getSectionInnerHTML(). Use those
        // exact methods when available, but intentionally DO NOT call
        // drawer.renderContents(): Dawn's renderContents() opens the drawer.
        // We want fresh contents without forcing it open.
        if (
          typeof drawer.getSectionsToRender === 'function' &&
          typeof drawer.getSectionInnerHTML === 'function'
        ) {
          try {
            drawer.getSectionsToRender().forEach(section => {
              const responseKey = section?.section || section?.id;
              const html = sections[responseKey];
              if (!html) return;

              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);

              if (!sectionElement) return;

              const nextHtml = drawer.getSectionInnerHTML(html, section.selector);
              if (typeof nextHtml === 'string') {
                sectionElement.innerHTML = nextHtml;
                renderedViaDawnContract = true;
              }
            });
          } catch (error) {
            console.warn('[Racepack Fuel Planner] Dawn cart-drawer section refresh fallback used.', error);
          }
        }

        // Fallback for customised Dawn forks: update #CartDrawer inside the
        // existing <cart-drawer> custom element. Never replace the outer custom
        // element itself, otherwise its constructor/event bindings are lost.
        if (!renderedViaDawnContract && drawerHtml) {
          const parsed = new DOMParser().parseFromString(String(drawerHtml), 'text/html');
          const incomingCartDrawer = parsed.querySelector('#CartDrawer');
          const liveCartDrawer = drawer.querySelector('#CartDrawer');

          if (incomingCartDrawer && liveCartDrawer) {
            liveCartDrawer.innerHTML = incomingCartDrawer.innerHTML;
          }
        }

        // Dawn rebinds the overlay whenever drawer contents are redrawn.
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer), { once: true });
        }
      }
    }

    async refreshCartChrome() {
      try {
        const root = window.Shopify?.routes?.root || this.config.root || '/';
        const requestedSections = this.cartSectionsRequest();
        const base = `${location.pathname}${location.search || ''}`;
        const joiner = base.includes('?') ? '&' : '?';

        const [sectionsResponse, cartResponse] = await Promise.all([
          fetch(
            `${base || root}${joiner}sections=${encodeURIComponent(requestedSections.join(','))}`,
            { credentials: 'same-origin', cache: 'no-store' }
          ),
          fetch(root + 'cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          })
        ]);

        if (!sectionsResponse.ok) return;

        const sections = await sectionsResponse.json();
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        this.refreshCartSections(sections, cart);
      } catch (_) {}
    }

    lineHasProperties(item) {
      const properties = item?.properties || {};
      return Object.keys(properties).some(key => {
        const value = properties[key];
        return value !== null && value !== undefined && String(value) !== '';
      });
    }

    mergeableLinesForVariant(cart, variantId) {
      return (cart?.items || []).filter(item =>
        String(item.variant_id) === String(variantId) &&
        !this.lineHasProperties(item)
      );
    }

    mergeableVariantQuantity(cart, variantId) {
      return this.mergeableLinesForVariant(cart, variantId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }

    plannerFamilyEntry(familyKey) {
      return this.plannerLedger?.[familyKey] || null;
    }

    ledgerItemsForFamily(familyKey) {
      return (this.plannerFamilyEntry(familyKey)?.items || [])
        .map(item => ({
          id: Number(item.id),
          quantity: Math.max(0, Number(item.quantity || 0)),
          handle: item.handle || '',
          familyKey
        }))
        .filter(item => item.id && item.quantity > 0);
    }

    flattenPlannerLedger() {
      return Object.entries(this.plannerLedger || {}).flatMap(([familyKey, family]) =>
        (family?.items || []).map(item => ({
          id: Number(item.id),
          quantity: Math.max(0, Number(item.quantity || 0)),
          handle: item.handle || '',
          familyKey,
          referenceVariantId: family.referenceVariantId || item.id
        }))
      ).filter(item => item.id && item.quantity > 0);
    }

    buildFamilyCartUpdates(cart, familyKey, desiredItems) {
      const previousItems = this.ledgerItemsForFamily(familyKey);
      const oldById = new Map(previousItems.map(item => [String(item.id), Number(item.quantity || 0)]));
      const newById = new Map(desiredItems.map(item => [String(item.id), Number(item.quantity || 0)]));
      const ids = new Set([...oldById.keys(), ...newById.keys()]);
      const updates = {};

      ids.forEach(id => {
        const currentLines = this.mergeableLinesForVariant(cart, id);
        const currentTotal = currentLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const oldContribution = Math.min(Number(oldById.get(id) || 0), currentTotal);
        const desiredContribution = Math.max(0, Number(newById.get(id) || 0));
        const desiredTotal = Math.max(0, currentTotal - oldContribution + desiredContribution);

        if (currentLines.length) {
          // Preserve any additional property-free lines and adjust one concrete
          // line by its key. In normal Dawn carts there will be one such line.
          const [primary, ...others] = currentLines;
          const otherTotal = others.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          updates[primary.key] = Math.max(0, desiredTotal - otherTotal);
        } else if (desiredTotal > 0) {
          // update.js adds the variant when the ID doesn't currently exist.
          updates[id] = desiredTotal;
        }
      });

      return updates;
    }

    async applyPlannerFamilyContribution(familyKey, desiredItems, referenceVariantId) {
      const cart = await this.fetchCart();
      const updates = this.buildFamilyCartUpdates(cart, familyKey, desiredItems);

      if (Object.keys(updates).length) {
        const root = window.Shopify?.routes?.root || this.config.root || '/';
        const response = await fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            sections: this.cartSectionsRequest(),
            sections_url: `${location.pathname}${location.search || ''}`
          })
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.description || body.message || 'Could not update your cart.');
        }
        this.refreshCartSections(body.sections, body);
      }

      if (desiredItems.length) {
        this.plannerLedger[familyKey] = {
          referenceVariantId: Number(referenceVariantId),
          items: desiredItems.map(item => ({
            id: Number(item.id),
            quantity: Number(item.quantity),
            handle: item.handle || ''
          }))
        };
      } else {
        delete this.plannerLedger[familyKey];
      }

      this.persistPlannerState(this.plan ? 'results' : 'form');
    }

    async confirmFuelModal() {
      if (this.busy) return;

      const chosen = this.modalRows
        .map(row => ({ row, product: this.modalRowProduct(row) }))
        .filter(entry => entry.product && Number(entry.row.quantity) > 0);

      if (!chosen.length || !this.modalProduct || !this.modalFit || !this.modalFamilyKey) return;

      const familyKey = this.modalFamilyKey;
      const wasEditing = this.modalEditing;
      const referenceVariantId = Number(this.modalProduct.id);
      const desiredItems = chosen.map(entry => ({
        id: Number(entry.product.id),
        quantity: Number(entry.row.quantity),
        handle: entry.product.handle || ''
      }));

      if (this.config.previewMode) {
        this.plannerLedger[familyKey] = {
          referenceVariantId,
          items: desiredItems
        };
        this.cartSelection = {
          referenceVariantId,
          items: this.flattenPlannerLedger()
        };
        this.cachedReferenceVariantId = referenceVariantId;
        this.baseProduct = this.modalProduct;
        this.selection = this.modalFit.pack;
        this.persistPlannerState('results');
        this.closeFuelModal();
        this.renderProductBrowser();
        this.updateSodium();
        this.showToast(wasEditing ? 'Selection updated ✓' : 'Added to cart! ✓');
        return;
      }

      this.busy = true;
      this.updateFuelModalSummary();
      const errorNode = this.$('[data-fuel-modal-error]');
      if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }

      try {
        await this.applyPlannerFamilyContribution(familyKey, desiredItems, referenceVariantId);

        this.cachedReferenceVariantId = referenceVariantId;
        this.baseProduct = this.modalProduct;
        this.selection = this.modalFit.pack;
        this.closeFuelModal();

        await this.reconcilePlannerCart();
        this.updateSodium();
        this.renderProductBrowser();

        this.showToast(wasEditing ? 'Selection updated ✓' : 'Added to cart! ✓');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } catch (error) {
        if (errorNode) {
          errorNode.hidden = false;
          errorNode.textContent = error.message || 'Could not update your cart.';
        }
      } finally {
        this.busy = false;
        this.updateFuelModalSummary();
      }
    }

    async removeFuelSelection(familyKey = '') {
      if (this.busy) return;

      const key = familyKey || this.modalFamilyKey;
      if (!key) return;

      this.busy = true;
      try {
        if (this.config.previewMode) {
          delete this.plannerLedger[key];
          this.cartSelection = {
            referenceVariantId: this.cachedReferenceVariantId,
            items: this.flattenPlannerLedger()
          };
          if (!this.cartSelection.items.length) this.cartSelection = null;
        } else {
          await this.applyPlannerFamilyContribution(key, [], this.cachedReferenceVariantId || 0);
        }

        this.closeFuelModal();
        await this.reconcilePlannerCart();
        this.persistPlannerState(this.plan ? 'results' : 'form');

        if (this.plan) {
          this.updateSodium();
          this.renderProductBrowser();
        }

        this.showToast('Removed from cart');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } catch (error) {
        this.showToast(error.message || 'Could not update cart');
      } finally {
        this.busy = false;
      }
    }

    persistPlannerState(view = null) {
      try {
        const answers = {};
        for (const element of this.form?.elements || []) {
          if (!element.name || element.disabled) continue;
          if (element.type === 'radio') {
            if (element.checked) answers[element.name] = element.value;
          } else if (element.type === 'checkbox') {
            answers[element.name] = element.checked ? String(element.value || 'on') : '__unchecked__';
          } else {
            answers[element.name] = element.value;
          }
        }
        localStorage.setItem(this.storageKey, JSON.stringify({
          version: 1,
          savedAt: Date.now(),
          view: view || (this.plan ? 'results' : 'form'),
          answers,
          referenceVariantId: this.baseProduct?.id || this.cartSelection?.referenceVariantId || null,
          plannerLedger: this.plannerLedger
        }));
      } catch (_) {}
    }

    restorePlannerState() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        const state = JSON.parse(raw);
        if (!state || !state.answers || Date.now() - Number(state.savedAt || 0) > 1000 * 60 * 60 * 24 * 30) return;

        Object.entries(state.answers).forEach(([name, value]) => {
          const controls = [...this.form.elements].filter(element => element.name === name);
          if (!controls.length) return;
          controls.forEach(control => {
            if (control.type === 'radio') control.checked = String(control.value) === String(value);
            else if (control.type === 'checkbox') control.checked = value !== '__unchecked__';
            else control.value = value;
          });
        });
        this.cachedReferenceVariantId = state.referenceVariantId || null;
        this.plannerLedger = state.plannerLedger && typeof state.plannerLedger === 'object'
          ? state.plannerLedger
          : {};
        this.restoredView = state.view === 'results' ? 'results' : 'form';
        this.syncChoices();
        this.syncDuration();
      } catch (_) {}
    }

    applyCartSelectionToPlan() {
      if (!this.plan || !this.cartSelection?.items?.length) return;
      const referenceId = this.cartSelection.referenceVariantId || this.cachedReferenceVariantId || this.cartSelection.items[0]?.id;
      const reference = this.productPool.find(product => String(product.id) === String(referenceId));
      if (!reference) {
        this.renderProductBrowser();
        return;
      }
      this.baseProduct = reference;
      this.selection = this.productFit(reference).pack;
      this.updateSodium();
      this.renderProductBrowser();
    }

    async reconcilePlannerCart() {
      if (this.config.previewMode) return;
      try {
        const cart = await this.fetchCart();
        const nextLedger = {};

        Object.entries(this.plannerLedger || {}).forEach(([familyKey, family]) => {
          const keptItems = [];

          (family?.items || []).forEach(item => {
            const currentQuantity = this.mergeableVariantQuantity(cart, item.id);
            if (currentQuantity <= 0) return;

            // If the customer reduced the merged cart line manually, clamp the
            // remembered planner contribution to what still exists.
            const contribution = Math.min(
              Math.max(0, Number(item.quantity || 0)),
              currentQuantity
            );
            if (contribution <= 0) return;

            keptItems.push({
              id: Number(item.id),
              quantity: contribution,
              handle: item.handle || ''
            });
          });

          if (keptItems.length) {
            nextLedger[familyKey] = {
              referenceVariantId: Number(family.referenceVariantId || keptItems[0].id),
              items: keptItems
            };
          }
        });

        this.plannerLedger = nextLedger;
        const items = this.flattenPlannerLedger();

        if (!items.length) {
          this.cartSelection = null;
          this.baseProduct = null;
          this.selection = null;
          if (this.plan) {
            this.updateSodium();
            this.renderProductBrowser();
          }
          this.persistPlannerState();
          return;
        }

        const preferredReference = String(this.cachedReferenceVariantId || '');
        const preferredFamily = Object.values(this.plannerLedger).find(family =>
          String(family.referenceVariantId || '') === preferredReference
        ) || Object.values(this.plannerLedger)[0];

        const referenceVariantId = Number(
          preferredFamily?.referenceVariantId ||
          items[0]?.referenceVariantId ||
          items[0]?.id
        );

        this.cartSelection = {
          referenceVariantId,
          items
        };

        this.cachedReferenceVariantId = referenceVariantId;
        this.applyCartSelectionToPlan();
        this.persistPlannerState();

        // External cart changes (for example removing a line in Dawn's drawer)
        // must immediately update SELECTED / EDIT SELECTION states.
        if (this.plan) {
          this.updateSodium();
          this.renderProductBrowser();
        }
      } catch (error) {
        console.warn('[Racepack Fuel Planner] Cart reconciliation skipped.', error);
      }
    }

    formatHydrationName(product, count = 1) {
      const plural = count === 1 ? '' : 's';
      switch (product.format) {
        case 'tablet': return `salt tablet${plural}`;
        case 'capsule': return `salt capsule${plural}`;
        case 'drinkmix': return `drink-mix portion${plural}`;
        case 'electrolyte': return `electrolyte tablet portion${plural}`;
        default: return `portion${plural}`;
      }
    }

    productLimitCopy(product) {
      const parts = [];
      if (Number.isInteger(product.maxServings) && product.maxServings >= 1) {
        parts.push(`Listed maximum: ${product.maxServings} serving${product.maxServings === 1 ? '' : 's'} per session.`);
      }
      if (Number.isInteger(product.maxServingsPerDay) && product.maxServingsPerDay >= 1) {
        parts.push(`Listed maximum: ${product.maxServingsPerDay} serving${product.maxServingsPerDay === 1 ? '' : 's'} per day.`);
      }
      return parts.join(' ');
    }

    paintFuel() {
      const s = this.selection;
      const shopping = this.$('[data-shopping]');
      const packEmpty = this.$('[data-pack-empty]');

      if (!s) {
        if (shopping) shopping.hidden = true;
        if (packEmpty) packEmpty.hidden = false;
        const heading = this.$('[data-recommended-pack-title]');
        if (heading) heading.textContent = 'YOUR RECOMMENDED PACK';
        const miniFit = this.$('[data-mini-fuel-fit]');
        const miniWarning = this.$('[data-mini-fuel-warning]');
        if (miniFit) miniFit.replaceChildren();
        if (miniWarning) {
          miniWarning.hidden = true;
          miniWarning.textContent = '';
        }
        return;
      }

      const product = s.product;
      const status = this.$('[data-cart-status]');
      if (status) status.textContent = '';

      if (shopping) shopping.hidden = false;
      if (packEmpty) packEmpty.hidden = true;

      const title = this.$('[data-mini-fuel-title]');
      const meta = this.$('[data-mini-fuel-meta]');
      const price = this.$('[data-mini-fuel-price]');
      const statusBadge = this.$('[data-mini-fuel-status]');
      const miniFit = this.$('[data-mini-fuel-fit]');
      const miniWarning = this.$('[data-mini-fuel-warning]');
      const heading = this.$('[data-recommended-pack-title]');
      const fit = this.productFit(product);

      if (heading) heading.textContent = fit.recommended ? 'YOUR RECOMMENDED PACK' : 'YOUR SELECTED PACK';
      if (title) title.textContent = `${this.displayTitle(product)} — ${product.variant}`;
      if (statusBadge) {
        statusBadge.textContent = fit.recommended ? 'MATCH' : 'ALTERNATIVE';
        statusBadge.classList.toggle('is-alternative', !fit.recommended);
      }
      if (meta) {
        meta.textContent =
          `${s.servings} serving${s.servings === 1 ? '' : 's'} planned · ${fit.rate.toFixed(1)}g/h · ${this.purchaseCopy(s, product)}`;
      }
      if (miniFit) {
        miniFit.replaceChildren(this.fitMeter(fit));
      }
      if (miniWarning) {
        miniWarning.hidden = fit.recommended || !fit.issues.length;
        miniWarning.textContent = fit.recommended || !fit.issues.length
          ? ''
          : `Alternative: ${fit.issues[0]}`;
      }
      if (price) price.textContent = this.money(s.price);

      const schedule = this.$('[data-schedule]');
      if (schedule) {
        schedule.replaceChildren();
        s.times.forEach(time => {
          schedule.append(this.node(
            'li',
            `Around ${Math.floor(time / 60)}:${String(time % 60).padStart(2, '0')} — ` +
            `1 serving (${product.carbs} g carbohydrate).` +
            (product.requiresWater === true ? ' Take with water according to the product label.' : '') +
            (['drink', 'drinkmix'].includes(product.format) ? ' Sip the prepared serving over this interval; follow dilution instructions.' : '')
          ));
        });
      }

      const scheduleNote = this.$('[data-schedule-note]');
      if (scheduleNote) {
        scheduleNote.textContent =
          'Illustrative timings for all selected fuel servings, including any already owned. Practise the complete plan in training.';
      }
    }

    updateSodium() {
      const p = this.plan;
      const base = this.baseProduct
        ? this.engine.pack(this.baseProduct, p, this.input)
        : this.selection;

      this.sodium = this.engine.hydration(this.products, this.input, p, base);
      const h = this.sodium;
      const waitingForFuel = !base && p.remainingLow > 0;

      // Fuel is chosen first. Keep the sodium target visible, but do not
      // silently choose an electrolyte product before the customer picks fuel.
      if (waitingForFuel && h) {
        h.selected = null;
      }

      this.selection = h.selected?.fuel || base;
      const s = this.selection;
      this.paintFuel();

      const sodiumRange = h.targetRange || p.sodiumTotalRange || [h.target, h.target];
      this.$('[data-sodium-summary]').textContent =
        `Sodium target: ${sodiumRange[0]}–${sodiumRange[1]} mg over this session ` +
        `(${p.sodiumRange ? p.sodiumRange.join('–') : Math.round(p.sodiumRate)} mg/hour). ` +
        `Selected fuel contributes ${h.fuelSodium} mg and other known sources contribute ${h.externalSodium} mg. ` +
        (h.gap > 0
          ? `${h.gap} mg remains to reach the lower edge of the sodium range.`
          : `The current combination reaches the sodium planning range.`);

      this.$('[data-sodium-assumption]').textContent = p.simpleHydration
        ? `Starting estimate for ${this.input.conditions === 'hot' ? 'hot/humid' : 'mild'} weather, ` +
          `${this.input.effort} effort and ${p.minutes} minutes. Suggested range: ${p.sodiumRange.join('–')} mg/hour ` +
          `(${p.sodiumTotalRange.join('–')} mg over this race). This is a general planning estimate, not a measurement of your sweat loss. ` +
          'Other drinks and foods are not counted; adjust the complete plan if you use them. Practise in training, follow labels and drink to thirst.'
        : `Model: ${p.fluidRate} L/hour × ${p.sodiumConcentration} mg/L × ${(p.minutes / 60).toFixed(2)} hours. ` +
          'General intake reference; not measured sweat losses.';

      const select = this.$('[data-hydration-select]');
      if (!select) return;
      select.replaceChildren();
      h.choices.forEach(x => {
        const o = this.node(
          'option',
          `${x.product.title} — ${x.product.variant} · ${x.product.sodium} mg sodium + ${x.product.carbs} g carb/portion`
        );
        o.value = String(x.product.id);
        select.append(o);
      });

      this.$('[data-hydration-choice]').hidden = waitingForFuel || !h.choices.length;

      if (waitingForFuel) {
        this.input.hydrationId = '';
        this.$('[data-sodium-product]').textContent =
          'Choose your fuel above first. We’ll then calculate how much additional sodium is still needed and show matching electrolyte options.';
      } else if (h.selected) {
        select.value = String(h.selected.product.id);
        this.input.hydrationId = String(h.selected.product.id);
        const n = h.selected;
        const unitName = this.formatHydrationName(n.product, n.servings);
        const useText =
          `Use ${n.servings} ${unitName} (${n.sodium} mg sodium + ${n.carbs} g carbohydrate). ` +
          `Buy ${n.units} whole unit${n.units === 1 ? '' : 's'} × ${n.product.servings} portions. ` +
          `Selected sources provide ${h.total} mg sodium total versus your ${sodiumRange[0]}–${sodiumRange[1]} mg target. ` +
          (n.sodiumMetrics?.withinRange
            ? 'This combination lands inside the sodium target range. '
            : n.sodiumMetrics?.below > 0
              ? `This is ${Math.round(n.sodiumMetrics.below)} mg below the sodium range; it is the closest verified option under the current constraints. `
              : `This is ${Math.round(n.sodiumMetrics?.above || 0)} mg above the sodium range; it is the closest verified option under the current constraints. `) +
          (n.mixingMl != null
            ? `Mix with ${(n.mixingMl / 1000).toFixed(2)} L water in total, following the label. `
            : 'Take/use with water as directed on the label. ') +
          `Sodium product cost: ${this.money(n.price)}.`;

        this.$('[data-sodium-product]').textContent = useText;
      } else {
        this.input.hydrationId = '';
        this.$('[data-sodium-product]').textContent = h.gap === 0
          ? 'No additional sodium product is needed to cover this reference.'
          : this.input.sodiumFormat === 'none'
            ? 'You selected no additional sodium product; the reference gap is not covered.'
            : `No verified sodium product meets the selected format, dietary, water, label, mixing-volume and combined-budget constraints. ` +
              `Quantity illustration only: ${h.referenceTablets} tablets at 200 mg sodium each OR ${h.referenceElectrolytes} portions at 500 mg each. ` +
              'These example counts are not product recommendations. Verify your exact label before use. The planner does not invent a product quantity merely to hit one exact sodium number.';
      }

      const sodiumNoteParts = [
        this.customerNote(h.selected?.product.note),
        h.selected ? this.productLimitCopy(h.selected.product) : ''
      ].filter(Boolean);
      this.$('[data-sodium-note]').textContent = sodiumNoteParts.join(' ');

      const sodiumLink = this.$('[data-sodium-link]');
      sodiumLink.hidden = !h.selected;
      sodiumLink.href = this.config.previewMode ? '#' : h.selected?.product.url || '#';
      sodiumLink.textContent = this.config.previewMode
        ? 'Preview product — live product details appear on the storefront.'
        : 'View sodium product label →';

      let bandText = '';
      if (p.band && !h.selected?.carbs && this.input.band !== '151+' && !this.input.finishMinutes && s) {
        const counts = p.bandBounds.map(minutes => {
          const i = { ...this.input, band: '', minutes };
          return this.engine.pack(s.product, this.engine.calculate(i), i).servings;
        });
        bandText =
          ` Across your selected band, this same product would give approximately ${Math.min(...counts)}–${Math.max(...counts)} servings, ` +
          'subject to the same verified product limits and tolerance checks. Exact time refines the result.';
      }

      const selectedFuelAndHydrationCarbs =
        (s?.carbs || 0) + p.external + (h.selected?.carbs || 0);

      this.$('[data-quantity-summary]').textContent =
        (s
          ? `${s.servings} ${s.product.format === 'gel' ? 'gels' : 'fuel servings'} selected; ` +
            `${selectedFuelAndHydrationCarbs} g (${4 * selectedFuelAndHydrationCarbs} kcal) from selected fuel plus known other carbohydrate. ` +
            `${(selectedFuelAndHydrationCarbs / (p.minutes / 60)).toFixed(1)} g/hour after whole-serving rounding, compared with your ${p.rateLow}–${p.rateHigh} g/hour planning range.`
          : p.remainingLow === 0
            ? 'Your already-planned carbohydrate reaches the lower edge of the planning range; no additional fuel serving is required.'
            : 'Fuel count pending: choose a verified product matching your answers.') +
        (h.selected
          ? ` Sodium: ${h.total} mg total versus ${sodiumRange[0]}–${sodiumRange[1]} mg target ` +
            `(${h.selected.servings} ${this.formatHydrationName(h.selected.product, h.selected.servings)} selected).`
          : ` Sodium still to lower target edge: ${h.gap} mg.`) +
        bandText;

      const knownCaffeine = (s?.totalCaffeine || p.externalCaffeine) + (h.selected?.caffeine || 0);
      const caffeineThreshold = Number(p.caffeineReviewThreshold || 200);
      if (knownCaffeine > caffeineThreshold) {
        this.$('[data-sodium-product]').textContent +=
          ` Combined known caffeine exceeds the planner’s ${caffeineThreshold} mg review reference${p.weight ? ` (~3 mg/kg at ${p.weight} kg)` : ''}; review all sources before purchasing.`;
      }

      const miniHydration = this.$('[data-mini-hydration]');
      const miniHydrationTitle = this.$('[data-mini-hydration-title]');
      const miniHydrationMeta = this.$('[data-mini-hydration-meta]');
      const miniHydrationPrice = this.$('[data-mini-hydration-price]');
      const miniTotal = this.$('[data-mini-cart-total]');

      if (miniHydration) miniHydration.hidden = !h.selected;
      if (h.selected) {
        if (miniHydrationTitle) {
          miniHydrationTitle.textContent = `${this.displayTitle(h.selected.product)} — ${h.selected.product.variant}`;
        }
        if (miniHydrationMeta) {
          miniHydrationMeta.textContent =
            `${h.selected.servings} ${this.formatHydrationName(h.selected.product, h.selected.servings)} planned · ` +
            this.purchaseCopy(h.selected, h.selected.product);
        }
        if (miniHydrationPrice) miniHydrationPrice.textContent = this.money(h.selected.price);
      }

      if (miniTotal) {
        miniTotal.textContent = this.money((s?.price || 0) + (h.selected?.price || 0));
      }

      const cartButton = this.$('[data-cart]');
      if (cartButton) {
        cartButton.disabled = this.busy || waitingForFuel || !this.cartItems().length;
        cartButton.textContent = waitingForFuel
          ? 'SELECT A FUEL ABOVE FIRST'
          : this.cartItems().length
            ? 'ADD SELECTED PACK TO CART →'
            : 'NO PURCHASE NEEDED';
      }

      if (!this.hydrationListener) {
        select.addEventListener('change', () => {
          this.input.hydrationId = select.value;
          this.updateSodium();
          this.showToast('Hydration option updated ✓');
        }, { signal: this.abort.signal });
        this.hydrationListener = true;
      }
    }

    cartItems() {
      if (!this.plan) return [];

      const s = this.selection;
      const h = this.sodium?.selected;

      // Selecting an alternative is a deliberate customer choice. Fit warnings
      // remain visible in the planner, but they do not prevent purchasing.
      if (!s && this.plan.remainingLow > (h?.carbs || 0)) return [];

      return [s, h]
        .filter(x => x && x.units > 0 && Number.isSafeInteger(Number(x.product?.id)))
        .map(x => ({ id: Number(x.product.id), quantity: x.units }));
    }

    async addCart() {
      if (this.busy || !this.cartItems().length) return;

      const items = this.cartItems();

      if (this.config.previewMode) {
        this.$('[data-cart-status]').textContent =
          'Preview only: ' +
          items.map(x => x.quantity + ' whole unit(s) of variant ' + x.id).join(' + ') +
          '. No real Shopify cart was changed.';
        return;
      }

      const chosen = [this.selection, this.sodium?.selected]
        .filter(x => x && x.units > 0);

      this.busy = true;
      this.$('[data-cart]').disabled = true;
      this.$('[data-cart-status]').textContent = 'Checking availability and current prices…';
      this.form.inert = true;
      this.querySelectorAll('[data-action="select-product"]').forEach(btn => { btn.disabled = true; });
      const hydrationSelect = this.$('[data-hydration-select]');
      if (hydrationSelect) hydrationSelect.disabled = true;

      let attemptedAdd = false;

      try {
        const root = window.Shopify?.routes?.root || this.config.root;

        for (const selection of chosen) {
          const product = selection.product;
          const response = await fetch(
            root + 'products/' + encodeURIComponent(product.handle) + '.js',
            {
              headers: { Accept: 'application/json' },
              cache: 'no-store',
              credentials: 'same-origin'
            }
          );

          if (!response.ok) {
            throw new Error('Could not verify this product. Please view the product or try again later.');
          }

          const live = await response.json();
          const variant = live.variants.find(v => String(v.id) === String(product.id));

          if (!variant?.available || live.requires_selling_plan || variant.requires_selling_plan) {
            product.available = false;
            this.renderResults(this.selection?.product.id);
            throw new Error('This exact variant is unavailable. Please review the updated selection.');
          }

          if (Number(variant.price) !== Number(product.price)) {
            product.price = Number(variant.price);
            this.renderResults(this.selection?.product.id);
            throw new Error('The price changed. Review the updated matches and total before adding products again.');
          }
        }

        this.$('[data-cart-status]').textContent = 'Adding selected products…';
        attemptedAdd = true;

        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.description || body.message || 'We could not add the full quantity to your cart.');
        }

        const cartStatus = this.$('[data-cart-status]');
        const cartCheck = this.$('[data-cart-check]');
        if (cartStatus) cartStatus.textContent = 'Added to cart successfully.';
        if (cartCheck) cartCheck.hidden = false;
        this.showToast('Added to cart! ✓');
      } catch (error) {
        const cartStatus = this.$('[data-cart-status]');
        if (cartStatus) {
          cartStatus.textContent =
            error.message +
            (attemptedAdd
              ? ' Check your cart before retrying; a stock or connection error can leave a changed cart.'
              : '');
        }

        if (attemptedAdd) {
          const cartCheck = this.$('[data-cart-check]');
          if (cartCheck) cartCheck.hidden = false;
        }
      } finally {
        this.busy = false;
        this.form.inert = false;
        this.renderProductBrowser();
        const hydrationSelect = this.$('[data-hydration-select]');
        if (hydrationSelect) hydrationSelect.disabled = false;
        this.$('[data-cart]').disabled = attemptedAdd && !this.selection || !this.cartItems().length;
      }
    }

    download() {
      if (!this.plan) {
        this.$('[data-status]').textContent = 'Complete the planner first to download your own plan.';
        this.scroll(this.$('[data-wizard]'));
        return;
      }

      const p = this.plan;
      const s = this.selection;

      const lines = [
        'RACEPACK FUEL PLANNER',
        this.title(),
        this.$('[data-answer-summary]').textContent,
        `Duration: ${p.minutes} minutes`,
        `Carbohydrate planning range: ${p.rateLow}–${p.rateHigh} g/h (${p.totalLow}–${p.totalHigh} g total); starting aim ${p.target} g/h.`,
        `Already planned: ${p.external} g carbohydrate`,
        ...p.notes,
        'Before: Use familiar carbohydrate food and your practised pre-start routine.',
        'Hydration: ' + this.$('[data-hydration]').textContent,
        `Carbohydrate planning range: ${p.fuelCaloriesLow}–${p.fuelCaloriesHigh} kcal from carbohydrate.`,
        this.$('[data-time-assumption]').textContent,
        this.$('[data-sodium-summary]').textContent,
        this.$('[data-sodium-product]').textContent,
        this.$('[data-sodium-assumption]').textContent,
        this.$('[data-bmi-result]').textContent
      ];

      if (s) {
        lines.push(
          `Product: ${s.product.title} — ${s.product.variant}`,
          `Use ${s.servings} servings; ${s.ownedUsed} already owned; buy ${s.units} units (${s.product.servings} servings/unit).`,
          this.productLimitCopy(s.product),
          `Selected products total: ${this.money(s.price + (this.sodium?.selected?.price || 0))}`,
          `Fuel totals: ${s.carbs} g carbohydrate; ${s.caffeine} mg caffeine; ${s.sodium} mg sodium. Known combined caffeine: ${s.totalCaffeine} mg.`,
          ...Array.from(this.$('[data-schedule]').children).map(el => el.textContent),
          ...s.warnings
        );
      }

      if (this.sodium?.selected) {
        lines.push(this.productLimitCopy(this.sodium.selected.product));
      }

      lines.push(
        'After: Rehydrate and eat a meal or snack with carbohydrate and protein.',
        'General guidance for adults. Practise in training. Individual needs vary. Prices and availability can change.'
      );

      const url = URL.createObjectURL(
        new Blob([lines.filter(Boolean).join('\n\n')], { type: 'text/plain;charset=utf-8' })
      );
      const a = this.node('a');
      a.href = url;
      a.download = 'racepack-fuel-plan.txt';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button)) return;

      if (this.busy) {
        event.preventDefault();
        return;
      }

      switch (button.dataset.action) {
        case 'start':
          event.preventDefault();
          {
            const heading = this.$(`[data-step="${this.step}"] h3`);
            if (heading) heading.focus({ preventScroll: true });
            this.scroll(this.$('[data-wizard]'));
          }
          break;

        case 'back':
          this.showStep(Math.max(0, this.step - 1));
          break;

        case 'edit':
          this.showStep(0);
          {
            const results = this.$('[data-results]');
            const preview = this.$('[data-preview]');
            if (results) results.hidden = true;
            if (preview) preview.hidden = false;
          }
          break;

        case 'filter-format':
          this.productFilters.format = button.dataset.productFormat || 'all';
          this.showAllProducts = false;
          this.renderProductBrowser();
          break;

        case 'show-products':
          this.showAllProducts = !this.showAllProducts;
          this.renderProductBrowser();
          break;

        case 'select-product':
          event.preventDefault();
          this.selectProduct(button.dataset.productId);
          break;

        case 'modal-qty':
          event.preventDefault();
          this.changeModalQuantity(button.dataset.index, button.dataset.delta);
          break;

        case 'close-fuel-modal':
          event.preventDefault();
          this.closeFuelModal();
          break;

        case 'confirm-fuel-modal':
          event.preventDefault();
          this.confirmFuelModal();
          break;

        case 'remove-modal-family':
          event.preventDefault();
          this.removeFuelSelection(this.modalFamilyKey);
          break;

        case 'remove-fuel':
          this.removeFuelSelection();
          break;

        case 'cart':
          this.addCart();
          break;

        case 'download':
          this.download();
          break;
      }
    }
  });
})();
